import { useState } from "react"
import { Edit, MoreHorizontal, Plus, Shield } from "react-feather"
import { useNavigate } from "react-router-dom"
import { organizationApi } from "@/api/client/organization"
import type { OrganizationList200OrganizationsItem } from "@/api/generated/models"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { SectionError } from "@/components/ui/error"
import { SectionLoading } from "@/components/ui/loading"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { OrganizationFormModal } from "./components"

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })

export function OrganizationsPage() {
  const navigate = useNavigate()
  const { canCreate, canUpdate, organizationId } = usePermission()
  const { data, isPending, isError, refetch } = organizationApi.useOrganizationList()

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationList200OrganizationsItem | null>(null)

  const handleOpenAddModal = () => {
    setSelectedOrganization(null)
    setIsFormModalOpen(true)
  }

  const handleOpenEditModal = (organization: OrganizationList200OrganizationsItem) => {
    setSelectedOrganization(organization)
    setIsFormModalOpen(true)
  }

  if (isPending) {
    return <SectionLoading />
  }

  if (isError) {
    return (
      <SectionError
        message="組織情報を取得できませんでした。権限が不足しているか、通信に失敗した可能性があります。"
        onRetry={() => refetch()}
      />
    )
  }

  const organizations = data.organizations

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">組織管理</h1>

      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{organizations.length}件の組織が見つかりました</div>
        {canCreate("organizations") && (
          <div>
            <Button onClick={handleOpenAddModal}>
              <Plus className="mr-2" size={16} />
              組織を追加
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">組織名</TableHead>
              <TableHead className="whitespace-nowrap">スラッグ</TableHead>
              <TableHead className="whitespace-nowrap">所属管理者</TableHead>
              <TableHead className="whitespace-nowrap">ロール</TableHead>
              <TableHead className="whitespace-nowrap">IP制限</TableHead>
              <TableHead className="whitespace-nowrap">作成日</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {organizations.map((organization) => (
              <TableRow key={organization.id}>
                <TableCell
                  className={
                    canUpdate("organizations") ? "text-blue-600 cursor-pointer hover:underline" : ""
                  }
                  onClick={
                    canUpdate("organizations") ? () => handleOpenEditModal(organization) : undefined
                  }
                >
                  <div className="flex items-center gap-2">
                    {organization.name}
                    {organization.id === organizationId && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                        現在の組織
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{organization.slug}</TableCell>
                <TableCell>{organization.memberCount}</TableCell>
                <TableCell>{organization.roleCount}</TableCell>
                <TableCell>
                  {organization.ipRestrictionEnabled ? (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                      有効
                    </span>
                  ) : (
                    <span className="text-xs text-slate-500">無効</span>
                  )}
                </TableCell>
                <TableCell>{formatDate(organization.createdAt)}</TableCell>
                <TableCell>
                  {canUpdate("organizations") && (
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[120px]">
                        <DropdownMenuItem onClick={() => handleOpenEditModal(organization)}>
                          <Edit className="text-slate-600 mr-2" size={16} />
                          編集
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            navigate(`/organizations/${organization.id}/ip-restriction`)
                          }
                        >
                          <Shield className="text-slate-600 mr-2" size={16} />
                          IPアドレス制限
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {organizations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  組織が見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="mt-4 text-sm text-slate-500">
        組織固有のロールが0件でも「全組織」のロールは割り当てられます。業務用の権限を割り当てる場合は、先に「ロール管理」でロールを追加してください。
      </p>

      <OrganizationFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        organization={selectedOrganization}
        onRefetch={refetch}
      />
    </div>
  )
}
